const baseUrl = "https://erp-elsalab.odoo.com";

/**
 * Normalize search query for consistent encoding across browsers.
 * Arabic and other Unicode text can be stored in different forms (NFC vs NFD);
 * normalizing to NFC ensures the same string is sent to the API regardless of
 * browser/IME.
 */
export function normalizeSearchQuery(query) {
  if (query == null) return "";
  return String(query).trim().normalize("NFC");
}

/**
 * Percent-encode string as UTF-8 bytes. Same result in every browser,
 * avoiding encodeURIComponent quirks with Arabic/Unicode on some browsers.
 */
function percentEncodeUTF8(str) {
  const bytes = new TextEncoder().encode(str);
  return Array.from(bytes)
    .map((b) => "%" + b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Encode search query for use in URL (normalize + UTF-8 percent-encode).
 */
export function encodeSearchQueryForUrl(query) {
  const normalized = normalizeSearchQuery(query);
  if (!normalized) return "";
  return percentEncodeUTF8(normalized);
}

// Search products with suggestions (debounced) - exact API format
export async function searchProducts(searchQuery, limit = 5, language = "en") {
  const normalized = normalizeSearchQuery(searchQuery);
  if (normalized.length < 2) {
    return [];
  }

  // Use explicit UTF-8 percent-encoding so Arabic works in all browsers
  let url = `${baseUrl}/api/cs/products-grouped-brand?search_query=${encodeSearchQueryForUrl(
    normalized
  )}&page=0&limit=${limit}&groupby=search`;

  // Add language parameter if Arabic
  if (language === "ar") {
    url += "&lang=ar";
  }

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain; charset=UTF-8",
      },
      body: JSON.stringify({}),
    });

    if (!res.ok) {
      throw new Error(`Search failed with status ${res.status}`);
    }

    // Use the same decoding approach as mobile app
    const buffer = await res.arrayBuffer();
    const text = new TextDecoder("utf-8").decode(buffer);
    const data = JSON.parse(text);

    console.log("Search API response:", data); // Debug log

    if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
      const group = data.data[0] || {};
      const items = Array.isArray(group.products) ? group.products : [];
      console.log("Found products:", items.length); // Debug log

      return items.map((p) => ({
        id: String(p.id || p.template_id || ""),
        name: p.name || "",
        title: p.name || "",
        price: typeof p.price === "number" ? p.price : null,
        list_price: typeof p.list_price === "number" ? p.list_price : null,
        image:
          p.image_url_1920 ||
          p.image_url ||
          (p.id ? `https://erp-elsalab.odoo.com/web/image/${p.id}` : ""),
        image_url_1920: p.image_url_1920 || null,
        image_url: p.image_url || null,
        availability:
          p.stock && p.stock[0] && p.stock[0].total_free > 0
            ? "In stock"
            : "Out of stock",
        brand: p.att_model_name || "",
        colors: p.att_color ? [p.att_color] : [],
        sizes: p.att_size && p.att_size !== "-" ? [p.att_size] : [],
        category: p.category || null,
        package_quantity: p.package_quantity || 1,
        stock: p.stock || [],
        subcategs: group.subcategs || [],
      }));
    }

    return [];
  } catch (e) {
    console.warn("Search API error:", e.message);
    return [];
  }
}

// Full search with pagination (for search results page) - exact API format
export async function searchProductsFull(searchQuery, page = 0, limit = 20) {
  const normalized = normalizeSearchQuery(searchQuery);
  if (normalized.length < 2) {
    return { products: [], total: 0 };
  }

  // Use explicit UTF-8 percent-encoding so Arabic works in all browsers
  const url = `https://erp-elsalab.odoo.com/api/cs/products-grouped-brand?search_query=${encodeSearchQueryForUrl(
    normalized
  )}&page=${page}&limit=${limit}&groupby=search`;

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain; charset=UTF-8",
      },
      body: JSON.stringify({}),
    });

    if (!res.ok) {
      throw new Error(`Search failed with status ${res.status}`);
    }

    // Use the same decoding approach as mobile app
    const buffer = await res.arrayBuffer();
    const text = new TextDecoder("utf-8").decode(buffer);
    const data = JSON.parse(text);

    console.log("Full search API response:", data); // Debug log

    if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
      const group = data.data[0] || {};
      const items = Array.isArray(group.products) ? group.products : [];
      console.log("Found products in full search:", items.length); // Debug log

      const products = items.map((p) => ({
        id: String(p.id || p.template_id || ""),
        name: p.name || "",
        title: p.name || "",
        price: typeof p.price === "number" ? p.price : null,
        list_price: typeof p.list_price === "number" ? p.list_price : null,
        image:
          p.image_url_1920 ||
          p.image_url ||
          (p.id ? `https://erp-elsalab.odoo.com/web/image/${p.id}` : ""),
        image_url_1920: p.image_url_1920 || null,
        image_url: p.image_url || null,
        availability:
          p.stock && p.stock[0] && p.stock[0].total_free > 0
            ? "In stock"
            : "Out of stock",
        brand: p.att_model_name || "",
        colors: p.att_color ? [p.att_color] : [],
        sizes: p.att_size && p.att_size !== "-" ? [p.att_size] : [],
        category: p.category || null,
        package_quantity: p.package_quantity || 1,
        stock: p.stock || [],
      }));

      return {
        products,
        total: data.count || products.length,
        hasMore: products.length === limit,
        brands: group.brands || [],
        subcategs: group.subcategs || [],
        types: group.types || [],
      };
    }

    return {
      products: [],
      total: 0,
      hasMore: false,
      brands: [],
      subcategs: [],
      types: [],
    };
  } catch (e) {
    console.warn("Search API error:", e.message);
    return {
      products: [],
      total: 0,
      hasMore: false,
      brands: [],
      subcategs: [],
      types: [],
    };
  }
}
